/**
 * Inventory constants and the small rules the screens share.
 *
 * Mirrors Constants.ItemType / Constants.Stock on the backend and
 * src/utils/stock.ts in the mobile app. The server is authoritative - it
 * recomputes stock from the ledger and refuses anything that would go negative.
 * What is here exists so the forms can warn BEFORE the round trip, and so every
 * screen words it the same way.
 */

export const ITEM_TYPE = {
  SERVICE: "SERVICE_PRODUCT",
  COMPLETE: "COMPLETE_PRODUCT",
};

export const ITEM_TYPE_OPTIONS = [
  {
    value: ITEM_TYPE.COMPLETE,
    label: "Complete Product",
    hint: "Physical stock - quantity is tracked",
  },
  {
    value: ITEM_TYPE.SERVICE,
    label: "Service Product",
    hint: "A service - no stock is kept",
  },
];

export const STOCK_UNITS = [
  "PCS",
  "KG",
  "GRAM",
  "LITRE",
  "METER",
  "BOX",
  "DOZEN",
  "SET",
];

/** Stock at or below this (and above zero) reads as low. Matches the server. */
export const DEFAULT_LOW_STOCK_THRESHOLD = 5;

export const itemTypeLabel = (value) =>
  ITEM_TYPE_OPTIONS.find((option) => option.value === value)?.label ||
  "Complete Product";

/**
 * Whether an item carries stock. The single rule.
 *
 * Items saved before the itemType field existed have none until the migration
 * backfills them, and those are complete products - so a missing type must read
 * as tracked, not untracked.
 */
export const isStockTracked = (item) => {
  if (!item) return false;
  return (item.itemType || ITEM_TYPE.COMPLETE) === ITEM_TYPE.COMPLETE;
};

/** Quantities may be fractional; keep them to 3 dp like the ledger does. */
export const roundQty = (value) => {
  const n = Number(value);
  if (!isFinite(n)) return 0;
  return Math.round(n * 1000) / 1000;
};

export const formatQty = (value, unit) => {
  const text = String(roundQty(value));
  return unit ? `${text} ${unit}` : text;
};

/** 'OUT' | 'LOW' | 'OK'. */
export const stockState = (
  quantity,
  itemThreshold,
  globalThreshold = DEFAULT_LOW_STOCK_THRESHOLD,
) => {
  const qty = Number(quantity) || 0;
  if (qty <= 0) return "OUT";
  const threshold =
    Number(itemThreshold) > 0 ? Number(itemThreshold) : globalThreshold;
  if (threshold > 0 && qty <= threshold) return "LOW";
  return "OK";
};

/**
 * The state of an item, preferring what the server derived and falling back to
 * deriving it here. Returns null for anything untracked, so a caller can use it
 * directly as "should a badge be shown at all".
 */
export const itemStockState = (item) => {
  if (!isStockTracked(item)) return null;
  return item?.stockState || stockState(item?.currentStock, item?.lowStockThreshold);
};

/** MUI chip colours and wording for a stock state. */
export const stockChipStyle = (state) => {
  if (state === "OUT") return { label: "Out of stock", bg: "#FFEBEE", fg: "#C62828" };
  if (state === "LOW") return { label: "Low stock", bg: "#FFF1DF", fg: "#C2620E" };
  return { label: "In stock", bg: "#E4F6EA", fg: "#1E7D34" };
};

/** The unit a quantity is counted in, preferring the stock unit over the FBR one. */
export const unitOf = (item) => item?.stockUnit || item?.uom || "";

/**
 * Which lines ask for more than is on hand.
 *
 * Quantities are totalled PER ITEM first, so two lines of the same product are
 * measured against one stock figure rather than each passing on its own.
 * Untracked items drop out here, so no caller has to remember the rule.
 */
export const findShortfalls = (lines, itemsById, alreadyTaken = {}) => {
  const wanted = new Map();
  (lines || []).forEach((line) => {
    const itemId = line?.itemId;
    if (!itemId || !isStockTracked(itemsById?.[itemId])) return;
    const quantity = Math.abs(Number(line.quantity) || 0);
    if (quantity === 0) return;
    wanted.set(itemId, roundQty((wanted.get(itemId) || 0) + quantity));
  });

  const shortfalls = [];
  wanted.forEach((quantity, itemId) => {
    const item = itemsById?.[itemId];
    const available = roundQty(
      (Number(item?.currentStock) || 0) - (Number(alreadyTaken[itemId]) || 0),
    );
    if (quantity <= available) return;
    shortfalls.push({
      itemId,
      itemName: item?.name || "Item",
      requested: quantity,
      available,
      shortBy: roundQty(quantity - available),
    });
  });
  return shortfalls;
};

/** One readable sentence per shortfall, for the alert. */
export const shortfallMessage = (shortfalls) =>
  (shortfalls || [])
    .map((s) => `${s.itemName}: need ${s.requested}, ${s.available} in stock`)
    .join("\n");

/** How each movement type reads in the history. */
export const MOVEMENT_TYPE_LABEL = {
  RECEIVING: "Received",
  SALE: "Sold",
  ADJUSTMENT: "Adjusted",
  REVERSAL: "Reversed",
};
